import { Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import { type DateTime } from "luxon";
import { type DataNeeded, type Decision, type ProCon, type ProConRelationship } from "./types";

type ProConKind = Dtos.Decisioning.ProConKind;
type ProConImportance = Dtos.Decisioning.ProConImportance;
type ProConRelationshipType = Dtos.Decisioning.ProConRelationshipType;

/** Most important first; ties keep creation order. */
export const sortDataNeeded = (items: readonly DataNeeded[]): DataNeeded[] =>
	[...items].sort((a, b) => b.importance - a.importance);

/** The open requests an underwriter should see first, plus how many did not make the cut. */
export const topOpenRequests = (
	items: readonly DataNeeded[],
	n: number,
): { top: DataNeeded[]; remaining: number } => {
	const sorted = sortDataNeeded(items);
	return { top: sorted.slice(0, n), remaining: Math.max(sorted.length - n, 0) };
};

/** Newest decision that no other decision in the list supersedes. Deleted decisions are already absent. */
export const latestEffectiveDecision = (decisions: readonly Decision[]): Option<Decision> => {
	const superseded = new Set(decisions.map((decision) => decision.supersedesDecisionId));
	return decisions
		.filter((decision) => !superseded.has(decision.id))
		.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())[0];
};

/** Only the account's latest decision can be corrected; the API enforces the same rule. */
export const correctableDecisionId = (decisions: readonly Decision[]): Option<Decision["id"]> =>
	Option.map(latestEffectiveDecision(decisions), (latest) => latest.id);

const byMostRecent = <T extends { createdAt: DateTime }>(a: T, b: T): number =>
	b.createdAt.toMillis() - a.createdAt.toMillis();

const groupBy = <T, K>(items: readonly T[], keyOf: (item: T) => Option<K>): Map<K, T[]> => {
	const groups = new Map<K, T[]>();
	for (const item of items) {
		const key = keyOf(item);
		if (Option.isNone(key)) continue;
		const group = groups.get(key);
		if (group) group.push(item);
		else groups.set(key, [item]);
	}
	return groups;
};

/** Data needed items grouped by the pro/con they were requested for, most recent first. */
export const groupDataNeededByProCon = (
	items: readonly DataNeeded[],
): Map<ProCon["id"], DataNeeded[]> => {
	const groups = groupBy(items, (item) => item.proConId);
	for (const group of groups.values()) group.sort(byMostRecent);
	return groups;
};

/** Importance buckets rendered top to bottom: Override, Key, Material, General. */
const PRO_CON_IMPORTANCE_ORDER = [4, 3, 2, 1] as const satisfies readonly ProConImportance[];

const isInBucket = (item: ProCon, kind: ProConKind, importance: ProConImportance): boolean =>
	item.kind === kind && item.importance === importance;

/** Number of pros/cons already ranked in one (kind, importance) bucket. */
export const bucketSize = (
	items: readonly ProCon[],
	kind: ProConKind,
	importance: ProConImportance,
	excludeId?: ProCon["id"],
): number =>
	items.filter((item) => item.id !== excludeId && isInBucket(item, kind, importance)).length;

type ProConBucket = {
	importance: ProConImportance;
	items: ProCon[];
};

/**
 * Pros/cons of one kind, grouped by importance (highest first) then ranked ascending
 * within each bucket (0 = most influential). Empty buckets are omitted.
 */
export const groupActiveProCons = (items: readonly ProCon[], kind: ProConKind): ProConBucket[] =>
	PRO_CON_IMPORTANCE_ORDER.map((importance) => ({
		importance,
		items: items
			.filter((item) => isInBucket(item, kind, importance))
			.sort((a, b) => a.rank - b.rank),
	})).filter((bucket) => bucket.items.length > 0);

/** Moves the item at `fromIndex` to `toIndex`, shifting everything between. */
export const moveWithinBucket = <T>(
	items: readonly T[],
	fromIndex: number,
	toIndex: number,
): T[] => {
	if (fromIndex === toIndex) return [...items];
	const next = [...items];
	const [moved] = next.splice(fromIndex, 1);
	next.splice(toIndex, 0, moved);
	return next;
};

/**
 * Applies a new rank order to one active (kind, importance) bucket, leaving every other
 * item untouched. Mirrors the server's reorder endpoint for optimistic updates.
 */
export const reorderBucket = (
	items: readonly ProCon[],
	kind: ProConKind,
	importance: ProConImportance,
	ids: readonly ProCon["id"][],
): ProCon[] => {
	const rankById = new Map(ids.map((id, index) => [id, index]));
	return items.map((item) => {
		const rank = rankById.get(item.id);
		return rank !== undefined && isInBucket(item, kind, importance) ? { ...item, rank } : item;
	});
};

const INVERSE_RELATIONSHIP_LABELS: Record<ProConRelationshipType, string> = {
	mitigates: "mitigated by",
	compounds: "compounded by",
	explains: "explained by",
};

/** The verb as the viewer reads it: "mitigates" on the source, "mitigated by" on the target. */
export const relationshipVerb = (
	relationship: Pick<ProConRelationship, "type" | "sourceProConId">,
	viewerId: ProCon["id"],
): string =>
	relationship.sourceProConId === viewerId
		? relationship.type
		: INVERSE_RELATIONSHIP_LABELS[relationship.type];

/** The pro/con on the other end of a relationship from `id`. */
export const relatedProConId = (
	relationship: Pick<ProConRelationship, "sourceProConId" | "targetProConId">,
	id: ProCon["id"],
): ProCon["id"] =>
	relationship.sourceProConId === id ? relationship.targetProConId : relationship.sourceProConId;

export const canRelate = Dtos.Decisioning.canRelate;

/** Relationships whose source and target are both on the board. */
export const visibleRelationships = (
	relationships: readonly ProConRelationship[],
	proCons: readonly ProCon[],
): ProConRelationship[] => {
	const ids = new Set(proCons.map((item) => item.id));
	return relationships.filter(
		(relationship) => ids.has(relationship.sourceProConId) && ids.has(relationship.targetProConId),
	);
};

/** Relationships grouped under both pros/cons they connect, most recent first. */
export const groupRelationshipsByProCon = (
	relationships: readonly ProConRelationship[],
): Map<ProCon["id"], ProConRelationship[]> => {
	const groups = new Map<ProCon["id"], ProConRelationship[]>();
	for (const relationship of relationships) {
		for (const id of [relationship.sourceProConId, relationship.targetProConId]) {
			const group = groups.get(id);
			if (group) group.push(relationship);
			else groups.set(id, [relationship]);
		}
	}
	for (const group of groups.values()) group.sort(byMostRecent);
	return groups;
};
